import { Button, Dropdown, Flex, Spin, Typography, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Approve, ApplicationTypeEnum, ApproveTypeEnum } from "@/types/reimburse";
import { templateList } from "@/apis/reimburse";
import ApproveTemplateDetail from "./components/ApproveTemplateDetail";

interface ApproveTemplateProps {
  activeKey: string;
}

// ApplicationType 映射
const ApplicationTypeMap: Record<ApplicationTypeEnum, string> = {
  [ApplicationTypeEnum.REIMBURSEMENT]: "reimburse.reimbursement",
  [ApplicationTypeEnum.DAILY_SUPPLIES]: "reimburse.dailySupplies",
  [ApplicationTypeEnum.MATERIAL_OVER_ISSUE]: "reimburse.materialOverIssue",
};

// ApproveType 映射
const ApproveTypeMap: Record<ApproveTypeEnum, string> = {
  [ApproveTypeEnum.IN_ORDER]: "reimburse.inOrder",
  [ApproveTypeEnum.ANY]: "reimburse.any",
  [ApproveTypeEnum.LAST]: "reimburse.last",
};

const ApplicationTypeColorMap: Record<ApplicationTypeEnum, string> = {
  [ApplicationTypeEnum.REIMBURSEMENT]: "blue",
  [ApplicationTypeEnum.DAILY_SUPPLIES]: "orange",
  [ApplicationTypeEnum.MATERIAL_OVER_ISSUE]: "red",
};

const ApproveTemplate: FC<ApproveTemplateProps> = ({ activeKey }) => {
  const { t } = useTranslation();
  const { Title } = Typography;
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<Approve[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentApplicationType, setCurrentApplicationType] = useState<ApplicationTypeEnum>();

  useEffect(() => {
    if (activeKey === "6") fetchList();
    else setDataSource([]);
  }, [activeKey]);

  const { hasReimburse, hasDaily, hasOverPicking } = useMemo(() => {
    return {
      hasReimburse: !!dataSource.find((data) => data.applicationType === ApplicationTypeEnum.REIMBURSEMENT),
      hasDaily: !!dataSource.find((data) => data.applicationType === ApplicationTypeEnum.DAILY_SUPPLIES,),
      hasOverPicking: !!dataSource.find((data) =>data.applicationType === ApplicationTypeEnum.MATERIAL_OVER_ISSUE,),
    };
  }, [dataSource]);

  const fetchList = () => {
    setLoading(true);
    templateList()
      .then((res) => setDataSource(res?.body ?? []))
      .catch(() => {
        setDataSource([]);
      })
      .finally(() => setLoading(false));
  };

  const handleDetailOpen = (applicationType: ApplicationTypeEnum) => {
    setCurrentApplicationType(applicationType);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setCurrentApplicationType(undefined);
  };

  const handleDetailSuccess = (data: Approve) => {
    setDataSource((last) => {
      return last.map(app => app.applicationType === data.applicationType ? data : app)
    })
  };

  const addMenuItems: MenuProps["items"] = [
    {
      key: ApplicationTypeEnum.REIMBURSEMENT,
      label: t(ApplicationTypeMap[ApplicationTypeEnum.REIMBURSEMENT]),
      disabled: hasReimburse,
    },
    {
      key: ApplicationTypeEnum.DAILY_SUPPLIES,
      label: t(ApplicationTypeMap[ApplicationTypeEnum.DAILY_SUPPLIES]),
      disabled: hasDaily,
    },
    {
      key: ApplicationTypeEnum.MATERIAL_OVER_ISSUE,
      label: t(ApplicationTypeMap[ApplicationTypeEnum.MATERIAL_OVER_ISSUE]),
      disabled: hasOverPicking,
    },
  ];

  const columns: ColumnsType<Approve> = [
    {
      title: t("common.name"),
      dataIndex: "name",
      key: "name",
      width: 200,
      ellipsis: true,
    },
    {
      title: t("reimburse.applicationTypeLabel"),
      dataIndex: "applicationType",
      key: "applicationType",
      width: 150,
      render: (type: ApplicationTypeEnum) => (
        <Tag color={ApplicationTypeColorMap[type]}>{t(ApplicationTypeMap[type])}</Tag>
      ),
    },
    {
      title: t("reimburse.approveTypeLabel"),
      dataIndex: "approveType",
      key: "approveType",
      width: 150,
      render: (type: ApproveTypeEnum) => (
        <Tag color="green">{t(ApproveTypeMap[type])}</Tag>
      ),
    },
    {
      title: t("common.opt"),
      fixed: "right",
      key: "opt",
      width: 80,
      render: (_, record) => (
        <Button type="link" onClick={() => handleDetailOpen(record.applicationType)}>
          {t("common.edit")}
        </Button>
      ),
    },
  ];

  return (
    <Flex vertical style={{ width: "100%" }} gap={10}>
      <Flex justify="space-between" align="baseline" style={{ marginTop: -20 }}>
        <Title level={4}>{t("layout.approveTemplate")}</Title>
        {(!hasReimburse || !hasDaily || !hasOverPicking) && (
          <Dropdown
            menu={{
              items: addMenuItems,
              onClick: ({ key }) =>
                handleDetailOpen(Number(key) as ApplicationTypeEnum),
            }}
          >
            <Button type="primary">{t("common.add")}</Button>
          </Dropdown>)}
      </Flex>
      {loading ? (
        <Spin spinning={true} style={{ marginTop: 100 }} />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      )}
      {currentApplicationType && (
        <ApproveTemplateDetail
          open={detailOpen}
          applicationType={currentApplicationType}
          onClose={handleDetailClose}
          onSuccess={handleDetailSuccess}
        />
      )}
    </Flex>
  );
};

export default ApproveTemplate;